import React, { useCallback, useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Pagination from '@material-ui/lab/Pagination';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import BracketLibrariesApiService from '../../../../../services/api/bracketLibraries';
import { formatTimestamp } from '../../../../../helpers/time';

const useListStyles = makeStyles(theme => {
  return {
    item: {
      display: 'flex',
      borderBottom: '1px solid grey',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1)
    },
    detail: {
      display: 'flex',
      flexWrap: 'wrap',
      flexDirection: 'column'
    },
    changes: {
      display: 'flex',
      flexWrap: 'wrap',
      flexDirection: 'column'
    },
    tooth: {
      marginRight: theme.spacing(0.5)
    },
    timestamp: {
      width: 180,
      fontWeight: 500,
      flex: '180px 0 0'
    },
    diff: {
      fontSize: 'smaller'
    },
    diffPositive: {
      color: theme.palette.success.main
    },
    diffNegative: {
      color: theme.palette.error.main
    },
    reason: {
      marginRight: theme.spacing(1.5)
    },
    teethSection: {}
  };
});

const groupTeeth = teeth => {
  const grouped = Object.keys(teeth).reduce((acc, tooth) => {
    // Extract the prefix (first two characters)
    const prefix = tooth.slice(0, 2);

    // If the prefix doesn't exist in accumulator, create an array for it
    if (!acc[prefix]) {
      acc[prefix] = [];
    }

    // Add the tooth to its prefix group
    acc[prefix].push(tooth);
    return acc;
  }, {});

  return grouped;
};

const List = ({ items }) => {
  const classes = useListStyles();

  return (
    <div>
      {items.map(item => {
        const groupedTeeth = groupTeeth(item.changes);
        return (
          <div key={`item-${item.timestamp}`} className={classes.item}>
            <div className={classes.timestamp}>
              <Typography variant="subtitle1">{formatTimestamp(item.timestamp)}</Typography>
            </div>
            <div className={classes.detail}>
              <div className={classes.reason}>
                <Typography variant="body1">{item.description}</Typography>
              </div>
              <div className={classes.changes}>
                {['UR', 'UL', 'LR', 'LL'].map(section => {
                  if (!groupedTeeth[section]) {
                    return null;
                  }
                  return (
                    <div className={classes.teethSection} key={`teeth-${section}`}>
                      {groupedTeeth[section].sort().map(tooth => {
                        const change = item.changes[tooth];
                        return (
                          <Typography
                            variant="caption"
                            className={classes.tooth}
                            key={`tooth-${tooth}`}
                          >
                            {tooth}
                            <span
                              className={[
                                classes.diff,
                                change.diff > 0 ? classes.diffPositive : classes.diffNegative
                              ].join(' ')}
                            >
                              {change.diff > 0 ? `+${change.diff}` : `${change.diff}`}
                            </span>
                          </Typography>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  title: {
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingBottom: 0
  },
  titleContent: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  container: {
    width: 800,
    minHeight: 470,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  filter: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1)
  },
  empty: {
    display: 'flex',
    justifyContent: 'center'
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1)
  }
}));

const Changelog = ({ organizationId, bracketLibraryId, open, onClose, ...props }) => {
  const classes = useStyles();
  const initialState = {
    perPage: 10,
    totalPage: null,
    currentPage: null,
    items: [],
    total: null,
    fetching: false,
    empty: false
  };
  const [data, setData] = useState(initialState);

  const fetch = useCallback(
    page => {
      if (data.fetching) {
        return;
      }
      setData({
        ...data,
        fetching: true
      });
      const api = new BracketLibrariesApiService();
      api
        .getChangelog(organizationId, bracketLibraryId, {
          perPage: data.perPage,
          page
        })
        .then(response => {
          setData({
            ...data,
            items: response.data,
            currentPage: response.meta.current_page,
            total: response.meta.total,
            fetching: false,
            empty: response.meta.total === 0
          });
        })
        .catch(() => {
          // handle brackets without inventory
          setData({
            ...initialState,
            empty: true,
            currentPage: 1,
            fetching: false
          });
        });
    },
    [organizationId, bracketLibraryId, data, setData, initialState]
  );

  useEffect(() => {
    if (open && data.currentPage === null) {
      fetch(1);
    } else if (!open && data.currentPage !== null) {
      setData(initialState);
    }
  }, [data, open, fetch, setData, initialState]);

  return (
    <Dialog {...props} maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle className={classes.title} disableTypography>
        <div className={classes.titleContent}>
          <Typography variant="h5">Changelog</Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            size="small"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <Container className={classes.container}>
          {data.empty ? <div className={classes.empty}>Empty</div> : <List items={data.items} />}
          <div className={classes.filter}>
            {data.totalPage !== null && (
              <Pagination
                count={Math.ceil(data.total / data.perPage)}
                page={data.currentPage}
                onChange={(event, page) => fetch(page)}
              />
            )}
          </div>
        </Container>
      </DialogContent>
    </Dialog>
  );
};

Changelog.propTypes = {};

export default Changelog;
